import { useRef, useState } from "react";
import { UploadCloud, FileText, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { fmtBytes } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function AttachmentUploader({ files, onChange, testid = "attachment-upload" }) {
  const { settings } = useAuth();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const types = settings?.allowed_file_types || ["pdf", "jpg", "jpeg", "png", "webp"];

  const upload = async (list) => {
    if (!list.length) return;
    const fd = new FormData();
    Array.from(list).forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/attachments", fd);
      onChange([...files, ...data]);
      toast.success(`${data.length} file(s) uploaded securely`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div>
      <div data-testid={`${testid}-dropzone`} tabIndex={0} role="button" data-nav
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); if (!busy) input.current?.click(); } }}
        onClick={() => !busy && input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        className={`cursor-pointer border-2 border-dashed rounded-md px-4 py-6 text-center transition-colors duration-150 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${drag ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-emerald-400 bg-slate-50/60"}`}>
        {busy ? <Loader2 className="w-6 h-6 mx-auto text-emerald-700 animate-spin" /> : <UploadCloud className="w-6 h-6 mx-auto text-emerald-700" />}
        <div className="text-sm font-medium text-slate-700 mt-2">{busy ? "Uploading to secure storage…" : "Drop invoice images / PDF pages or click to browse"}</div>
        <div className="text-[11px] text-slate-400 mt-1 uppercase">{types.join(" · ")} · max {settings?.max_file_mb || 10} MB each · multiple allowed · Enter to browse, Tab to continue</div>
        <input ref={input} type="file" multiple hidden accept={types.map((t) => `.${t}`).join(",")} onChange={(e) => upload(e.target.files)} data-testid={`${testid}-input`} />
      </div>
      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid={`${testid}-list`}>
          {files.map((f, i) => (
            <li key={f.id} className="flex items-center gap-3 px-3 py-2 border border-slate-200 rounded bg-white text-sm">
              {f.content_type === "application/pdf" ? <FileText className="w-4 h-4 text-red-600" /> : <ImageIcon className="w-4 h-4 text-sky-600" />}
              <span className="flex-1 truncate">{f.file_name}</span>
              <span className="num text-[11px] text-slate-400">{fmtBytes(f.size)}</span>
              <button type="button" onClick={() => onChange(files.filter((x) => x.id !== f.id))} className="text-slate-400 hover:text-red-600" data-testid={`${testid}-remove-${i}`}><X className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
